import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function SellerCard({ userId, onMessage }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await API.get(`/profile/${userId}/profile`);
        setProfile(data);
      } catch (err) {
        console.error('Failed to fetch seller profile:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId]);

  if (loading) {
    return (
      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--cream)', animation: 'pulse 1.5s infinite' }} />
          <div style={{ flex: 1 }}>
            <div style={{ height: 16, background: 'var(--cream)', borderRadius: 4, marginBottom: 8, animation: 'pulse 1.5s infinite' }} />
            <div style={{ height: 12, background: 'var(--cream)', borderRadius: 4, width: '60%', animation: 'pulse 1.5s infinite' }} />
          </div>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const { user, reviews } = profile;

  // Star rating display
  const StarRating = ({ rating, count }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 14, fontWeight: 700 }}>
        {'⭐'.repeat(Math.floor(rating))}
        {rating % 1 >= 0.5 ? '⭐' : ''}
      </span>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--cherry)' }}>
        {rating.toFixed(1)}
      </span>
      <span style={{ fontSize: 13, color: 'var(--text-soft)' }}>
        ({count} {count === 1 ? 'review' : 'reviews'})
      </span>
    </div>
  );

  return (
    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 24 }}>
      {/* Header: Avatar + Name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'var(--cherry-light)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            fontWeight: 700,
            color: 'var(--cherry)',
            overflow: 'hidden',
            flexShrink: 0,
            cursor: 'pointer'
          }}
          onClick={() => navigate(`/profile/${userId}`)}
        >
          {user.avatar_url ? (
            <img src={`${API_BASE}${user.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            user.first_name?.[0]?.toUpperCase()
          )}
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 16,
              cursor: 'pointer',
              color: 'var(--text-dark)',
              textDecoration: 'none'
            }}
            onClick={() => navigate(`/profile/${userId}`)}
          >
            {user.first_name} {user.last_name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
            Member since {user.member_since}
          </div>
        </div>
      </div>

      {/* Rating */}
      <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
        <StarRating rating={user.rating_avg || 0} count={user.rating_count || 0} />
      </div>

      {/* Response Time */}
      {user.avg_response_hours !== null && (
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span>⏱</span>
          <span>
            Usually replies in <strong>{Math.round(user.avg_response_hours)} {user.avg_response_hours < 2 ? 'hour' : 'hours'}</strong>
          </span>
        </div>
      )}

      {/* Verification & Earned Badges */}
      {user.badges && user.badges.length > 0 && (
        <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {user.badges.map((badge, i) => (
            <div
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                background: badge.type === 'trusted' ? '#FFF3E0' : badge.type === 'quick' ? '#E8F5E9' : '#E3F2FD',
                border: `1px solid ${badge.type === 'trusted' ? '#FFB74D' : badge.type === 'quick' ? '#81C784' : '#64B5F6'}`,
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                color: badge.type === 'trusted' ? '#E65100' : badge.type === 'quick' ? '#2E7D32' : '#0D47A1'
              }}
            >
              <span>{badge.icon}</span>
              {badge.label}
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      <div style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--cherry)', marginBottom: 2 }}>
            {user.total_listings}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-soft)', fontWeight: 600 }}>Items listed</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#2E7D32', marginBottom: 2 }}>
            {(user.total_sales || 0) + (user.total_rentals || 0)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-soft)', fontWeight: 600 }}>Successful sales</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        <button
          onClick={() => navigate(`/profile/${userId}`)}
          style={{
            padding: '10px',
            background: 'white',
            border: '1px solid var(--border)',
            borderRadius: 10,
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
            color: 'var(--text-dark)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = 'var(--cherry)';
            e.target.style.background = '#FEF5F5';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = 'var(--border)';
            e.target.style.background = 'white';
          }}
        >
          👤 View Profile
        </button>
        <button
          onClick={onMessage}
          style={{
            padding: '10px',
            background: '#E3F2FD',
            border: '1px solid #1976D2',
            borderRadius: 10,
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
            color: '#1976D2'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#BBDEFB';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#E3F2FD';
          }}
        >
          💬 Message
        </button>
      </div>

      {/* Recent Reviews */}
      {reviews && reviews.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 12 }}>
            Recent reviews
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {reviews.slice(0, 3).map(review => (
              <div key={review.id} style={{ padding: 10, background: 'var(--cream)', borderRadius: 8, fontSize: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span>{'⭐'.repeat(review.rating)}</span>
                  <span style={{ fontWeight: 600, color: 'var(--text-dark)' }}>
                    {review.first_name} {review.last_name}
                  </span>
                  <span style={{ color: 'var(--text-soft)', fontSize: 11 }}>
                    {review.created_at_formatted}
                  </span>
                </div>
                {review.title && (
                  <div style={{ fontWeight: 600, marginBottom: 2, color: 'var(--text-dark)' }}>
                    {review.title}
                  </div>
                )}
                {review.body && (
                  <div style={{ color: 'var(--text-mid)', lineHeight: 1.4 }}>
                    {review.body.substring(0, 100)}
                    {review.body.length > 100 ? '...' : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
